import React, { useEffect, useRef, useState, useCallback } from 'react';
import './ImageZoomModal.css';
import { Move } from 'lucide-react';

interface ImageZoomModalProps {
    imageUrl: string | null;
    onClose: () => void;
    zoomScale: number;
    onZoomChange: (scale: number) => void;
}

interface Position {
    x: number;
    y: number;
}

export const ImageZoomModal: React.FC<ImageZoomModalProps> = ({
    imageUrl,
    onClose,
    zoomScale,
    onZoomChange
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState<Position>({ x: 0, y: 0 });
    const [lastPosition, setLastPosition] = useState<Position>({ x: 0, y: 0 });

    const handleZoomChange = useCallback((newScale: number) => {
        onZoomChange(newScale);
    }, [onZoomChange]);

    useEffect(() => {
        if (imageUrl) {
            setPosition({ x: 0, y: 0 });
            setLastPosition({ x: 0, y: 0 });
            handleZoomChange(1);
        }
    }, [imageUrl, handleZoomChange]);

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (imageUrl) {
            document.addEventListener('keydown', handleEsc);
            return () => document.removeEventListener('keydown', handleEsc);
        }
    }, [imageUrl, onClose]);

    useEffect(() => {
        if (!imageUrl || !containerRef.current || !imageRef.current) return;

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();
            e.stopPropagation();

            const delta = -e.deltaY * 0.001;
            const newScale = Math.min(5, Math.max(0.5, zoomScale + delta));

            if (position.x === 0 && position.y === 0) {
                handleZoomChange(newScale);
                return;
            }

            const container = containerRef.current!;
            const rect = container.getBoundingClientRect();

            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            const scaleRatio = newScale / zoomScale;
            const newX = mouseX - (mouseX - position.x) * scaleRatio;
            const newY = mouseY - (mouseY - position.y) * scaleRatio;

            setPosition({ x: newX, y: newY });
            setLastPosition({ x: newX, y: newY });
            handleZoomChange(newScale);
        };

        const container = containerRef.current;
        container.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, [imageUrl, zoomScale, position, handleZoomChange]);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (e.button !== 0) return;

        setIsDragging(true);
        setDragStart({ x: e.clientX, y: e.clientY });
        setLastPosition(position);

        if (containerRef.current) {
            containerRef.current.style.cursor = 'grabbing';
        }
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return;

        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;

        setPosition({
            x: lastPosition.x + deltaX,
            y: lastPosition.y + deltaY
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        setLastPosition(position);

        if (containerRef.current) {
            containerRef.current.style.cursor = zoomScale > 1 ? 'grab' : 'default';
        }
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length !== 1) return;

        const touch = e.touches[0];
        setIsDragging(true);
        setDragStart({ x: touch.clientX, y: touch.clientY });
        setLastPosition(position);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging || e.touches.length !== 1) return;

        const touch = e.touches[0];
        const deltaX = touch.clientX - dragStart.x;
        const deltaY = touch.clientY - dragStart.y;

        setPosition({
            x: lastPosition.x + deltaX,
            y: lastPosition.y + deltaY
        });
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
        setLastPosition(position);
    };

    const handleDoubleClick = () => {
        setPosition({ x: 0, y: 0 });
        setLastPosition({ x: 0, y: 0 });
        handleZoomChange(1);
    };

    const handleZoomIn = () => {
        const newScale = Math.min(5, zoomScale + 0.3);
        handleZoomChange(newScale);
    };

    const handleZoomOut = () => {
        const newScale = Math.max(0.5, zoomScale - 0.3);
        handleZoomChange(newScale);

        if (newScale === 1) {
            setPosition({ x: 0, y: 0 });
            setLastPosition({ x: 0, y: 0 });
        }
    };

    if (!imageUrl) return null;

    return (
        <div
            className="image-zoom-overlay"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div
                ref={containerRef}
                className="zoom-container"
                style={{ cursor: isDragging ? 'grabbing' : (zoomScale > 1 ? 'grab' : 'default') }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onDoubleClick={handleDoubleClick}
            >
                <img
                    ref={imageRef}
                    src={imageUrl}
                    alt="Zoom"
                    className="zoomed-image"
                    draggable={false}
                    style={{
                        transform: `translate(${position.x}px, ${position.y}px) scale(${zoomScale})`,
                        transition: isDragging ? 'none' : 'transform 0.15s ease',
                        transformOrigin: '0 0'
                    }}
                />

                <div className="zoom-controls">
                    <button
                        type="button"
                        className="zoom-btn zoom-out"
                        onClick={handleZoomOut}
                        disabled={zoomScale <= 0.5}
                    >
                        −
                    </button>

                    <div className="zoom-indicator">
                        {Math.round(zoomScale * 100)}%
                    </div>

                    <button
                        type="button"
                        className="zoom-btn zoom-in"
                        onClick={handleZoomIn}
                        disabled={zoomScale >= 5}
                    >
                        +
                    </button>
                </div>

                <div className="zoom-help-tooltip">
                    {zoomScale > 1 ? (
                        <>
                            <Move size={14} />
                            <span>Arraste para mover • Scroll para zoom • Duplo clique para resetar</span>
                        </>
                    ) : (
                        <span>Use o scroll para dar zoom • Duplo clique para resetar</span>
                    )}
                </div>

                <button
                    type="button"
                    className="close-zoom"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onClose();
                    }}
                >
                    ✕
                </button>
            </div>
        </div>
    );
};